import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import { hospitalService } from '../../../services/modules/hospital/hospital.service';
import {
  IHospitalRecordParam,
  IHospitalRecordResult,
  IHospitalResult,
  ITag,
  ITagNumberColor,
} from '../../../models/hospital/HospitalModel';
import { hospitalRecordService } from '../../../services/modules/hospitalRecord/hospitalRecord.service';
import { toast } from 'react-toastify';
import type { GridToolbarHelpers } from '@inuxe/prime-odata-grid';
import { IQrResult } from '../../../models/hospital/QrModel';
import { RsValue } from '../../../models/base/BaseModel';
import { QrcodeResult } from 'html5-qrcode/esm/core';
import { signalrService } from '../../../services/signalr';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
  oDataFilter?: string;
}

/** Grid ve tek hastane export: en yüksek ID üstte. */
export const HOSPITAL_RECORD_DEFAULT_SORT_MODEL = [{ colId: 'id', sort: 'desc' as const }];

/** Çoklu hastane Excel export: eklenme tarihine göre (grid sırasından bağımsız). */
export const HOSPITAL_RECORD_EXPORT_SORT_MODEL = [{ colId: 'addDate', sort: 'desc' as const }];
export class HospitalFilesStore {
  rootStore: RootStore;
  selectedHospital: IHospitalResult | null = null;
  hospitalList: IHospitalResult[] = [];
  selectedFile: IHospitalRecordResult | null = null;
  error: string | null = null;
  gridHelpers: GridToolbarHelpers<IHospitalRecordResult> | null = null;
  hospitalRecordFromQr: QrcodeResult | null = null;
  selectedTag: ITag | null = null;
  tags: ITag[] = [];
  tagColors: ITagNumberColor[] = [];
  private tagColorsIntervalId: any = null;
  private tagColorsUpdatedHandler?: () => void;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setSelectedTag = (tag: ITag | null) => {
    this.selectedTag = tag;
  };
  getTagColors = async (): Promise<void> => {
    try {
      if (!this.selectedTag?.id) {
        runInAction(() => (this.tagColors = []));
        return;
      }
      const response = await hospitalService.getTagColorsByTagId(this.selectedTag.id);
      runInAction(() => {
        this.tagColors = response || [];
      });
    } catch (error) {
      console.error('Error fetching tag colors:', error);
    }
  };
  startLiveTagColors = (intervalMs: number = 1000) => {
    // Prefer SignalR over polling
    this.stopLiveTagColors();
    this.getTagColors();
    const token = localStorage.getItem('jwtToken') || sessionStorage.getItem('jwtToken') || '';
    signalrService
      .connect(() => token)
      .then(() => {
        this.tagColorsUpdatedHandler = () => {
          this.getTagColors();
          this.refreshSelectedTag();
        };
        signalrService.on('TagColorsUpdated', this.tagColorsUpdatedHandler);
      })
      .catch(err => console.error('SignalR connect error', err));
    // Fallback: very light polling if connection fails will be skipped
  };
  stopLiveTagColors = () => {
    if (this.tagColorsIntervalId) {
      clearInterval(this.tagColorsIntervalId);
      this.tagColorsIntervalId = null;
    }
    if (this.tagColorsUpdatedHandler) {
      try {
        signalrService.off('TagColorsUpdated', this.tagColorsUpdatedHandler);
      } catch {}
      this.tagColorsUpdatedHandler = undefined;
    }
    signalrService.disconnect().catch(() => {});
  };
  refreshSelectedTag = async (): Promise<void> => {
    if (this.selectedHospital && this.selectedTag) {
      const response = await hospitalService.getTagsByHospitalId(this.selectedHospital.id);
      const selectedTag = response.find(x => x.id === this.selectedTag?.id);

      if (selectedTag) {
        this.setSelectedTag(selectedTag);
      } else {
        this.setSelectedTag(null);
      }
    }
  };
  getTagsByHospitalId = async (hospitalId: number): Promise<void> => {
    try {
      const response = await hospitalService.getTagsByHospitalId(hospitalId);
      runInAction(() => {
        this.tags = response || [];
      });
    } catch (error) {
      console.error('Error fetching tags:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
    }
  };

  setGridHelpers = (helpers: GridToolbarHelpers<IHospitalRecordResult>) => {
    this.gridHelpers = helpers;
  };

  setSelectedHospital = (hospital: IHospitalResult | null) => {
    this.selectedHospital = hospital;
    if (hospital?.id) {
      this.getTagsByHospitalId(hospital.id);
    }
  };

  refreshGrid = () => {
    this.gridHelpers?.refreshGrid();
  };

  setSelectedFile = (file: IHospitalRecordResult) => {
    this.selectedFile = file;
  };

  setHospitals = (hospitals: IHospitalResult[]) => {
    this.hospitalList = hospitals;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.selectedHospital = null;
    this.selectedFile = null;
    this.error = null;
    this.selectedTag = null;
    this.tags = [];
    this.tagColors = [];
    this.stopLiveTagColors();
  };

  listHospitalRecord = async (
    params: GridParams
  ): Promise<{ rows: IHospitalRecordResult[]; lastRow: number; count: number }> => {
    try {
      const odataParams: ODataParams = {
        $count: true,
        $skip: params.startRow,
        $top: params.endRow - params.startRow,
      };

      if (params.oDataFilter) {
        odataParams.$filter = params.oDataFilter;
      } else if (params.filterModel) {
        const filters = Object.entries(params.filterModel).map(([field, filter]: [string, any]) => {
          const numericFields = ['id', 'hospitalId'];
          if (numericFields.includes(field)) {
            return `${field} eq ${filter.filter}`;
          }
          const value = String(filter.filter ?? '').toLowerCase().replace(/'/g, "''");
          return `contains(tolower(${field}), '${value}')`;
        });

        if (filters.length > 0) {
          odataParams.$filter = filters.join(' and ');
        }
      }

      const sortModel =
        params.sortModel && params.sortModel.length > 0
          ? params.sortModel
          : HOSPITAL_RECORD_DEFAULT_SORT_MODEL;
      const { colId, sort } = sortModel[0];
      odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
      if (this.selectedHospital?.id) {
        const hospitalId = parseInt(this.selectedHospital.id.toString(), 10);
        const hospitalFilter = `hospitalId eq ${hospitalId}`;
        odataParams.$filter = odataParams.$filter
          ? `${odataParams.$filter} and ${hospitalFilter}`
          : hospitalFilter;
      }

      if (this.selectedTag?.id) {
        const tagId = parseInt(this.selectedTag.id.toString(), 10);
        const tagFilter = `tagId eq ${tagId}`;
        odataParams.$filter = odataParams.$filter
          ? `${odataParams.$filter} and ${tagFilter}`
          : tagFilter;
      }

      const response: any = await hospitalRecordService.listHospitalRecords({
        params: odataParams,
      });

      return runInAction(() => {
        if (response) {
          const totalCount = response['@odata.count'] || 0;
          return {
            rows: response.value || [],
            lastRow: totalCount,
            count: totalCount,
          };
        }
        return {
          rows: [],
          lastRow: 0,
          count: 0,
        };
      });
    } catch (error) {
      console.error('Error fetching hospital records:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    }
  };
  newRowManuel = async (newRow: IHospitalRecordParam) => {
    try {
      const response: any = await hospitalRecordService.createHospitalRecord({
        ...newRow,
        hospitalId: this.selectedHospital?.id || 0,
      });

      if (response.error) {
        return response;
      }
      this.refreshGrid();
      // Tag renkleri ve seçili etiket bilgisi anında güncellensin
      await this.getTagColors();
      await this.refreshSelectedTag();
      return response;
    } catch (error) {
      console.error('Error creating hospital record:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    }
  };

  readFromQrCode = async (qrCode: string): Promise<RsValue<IQrResult>> => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await hospitalRecordService.readFromQrCode({ qrCodeUrl: qrCode });

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }
      runInAction(() => {
        this.error = null;
      });

      return response;
    } catch (error) {
      console.error('QR kod okuma hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        this.rootStore.uiStore.setLoading(false);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  readFromKn = async (knNumber: string): Promise<RsValue<IQrResult>> => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await hospitalRecordService.searchByKnNo({ kno: knNumber });

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }
      runInAction(() => {
        this.error = null;
      });

      return response;
    } catch (error) {
      console.error('KN kod okuma hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        this.rootStore.uiStore.setLoading(false);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  recordToDelete: IHospitalRecordResult | null = null;

  setRecordToDelete(record: IHospitalRecordResult | null) {
    this.recordToDelete = record;
  }

  deleteRecord = async (id: number) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await hospitalRecordService.delete(id);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      return response;
    } catch (error) {
      console.error('Silme hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        this.rootStore.uiStore.setLoading(false);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  selectedRecord: IHospitalRecordResult | null = null;

  setSelectedRecord(record: IHospitalRecordResult | null) {
    this.selectedRecord = record;
  }

  async updateRecord(id: number, data: Partial<IHospitalRecordParam>) {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await hospitalRecordService.update(id, {
        hospitalId: this.selectedHospital?.id,
        ...data,
      });

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Güncelleme başarısız');
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      // Güncellemeden sonra da senkronize et
      await this.getTagColors();
      await this.refreshSelectedTag();

      toast.success('Kayıt başarıyla güncellendi');
      return response;
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        toast.error('Güncelleme sırasında bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  }

  async searchFieldSuggestions(fieldName: string, searchTerm: string): Promise<string[]> {
    try {
      if (!this.selectedHospital?.id) {
        return [];
      }
      const escapedTerm = (searchTerm || '').replace(/'/g, "''");

      const odataParams: ODataParams = {
        $filter: `contains(tolower(${fieldName}), tolower('${escapedTerm}'))`,
        $select: fieldName,
        $orderby: `${fieldName} asc`,
        $top: 10,
        $count: true,
      };

      const response: any = await hospitalRecordService.listHospitalRecords({
        params: odataParams,
      });

      if (response && response.value) {
        return Array.from(
          new Set(
            response.value
              .map((item: any) => item[fieldName])
              .filter((value: any) => value != null && value !== '')
          )
        );
      }

      return [];
    } catch (error) {
      console.error('Error searching suggestions:', error);
      return [];
    }
  }

  async addHospitalTagPrefix(params: { hospitalId: number; prefix: string }) {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await hospitalService.addHospitalTagPrefix(params);
      runInAction(() => {
        if (response.error != true) this.refreshGrid();
      });
      return response;
    } catch (error) {
      console.error('Error adding hospital tag prefix:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  }
}
